
import React from "react";
import { Heart, FileText, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGuestSaves } from "@/hooks/use-guest-saves";
import { useAuth } from "@/components/auth/AuthProvider";
import { GuideButton } from "./sidebar/ActionButtons";
import { SidebarLinks } from "./sidebar/SidebarLinks";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { analytics } from "@/lib/analytics";

interface SidebarCTAProps {
  centerName: string;
  email?: string;
  nhSlug: string;
  countySlug: string;
  hiqaProfileUrl?: string;
}

export function SidebarCTA({ centerName, email, nhSlug, countySlug, hiqaProfileUrl }: SidebarCTAProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isNursingHomeSaved, saveNursingHome, removeSavedNursingHome } = useGuestSaves();
  const isSaved = isNursingHomeSaved(nhSlug);

  const handleSaveToggle = () => {
    if (isSaved) {
      removeSavedNursingHome(nhSlug);
      analytics.saveNursingHome(nhSlug, centerName, 'unsave', 'sidebar');
      toast.success("Removed from saved list", {
        description: `${centerName} has been removed from your saved list.`
      });
    } else {
      saveNursingHome({
        nh_slug: nhSlug,
        center_name: centerName,
        county_slug: countySlug,
        county_name: countySlug,
      });
      analytics.saveNursingHome(nhSlug, centerName, 'save', 'sidebar');
      if (!user) {
        toast.success("Saved for later!", {
          description: `${centerName} added to your saved list. Create an account to sync across devices.`,
          action: {
            label: "Create Account",
            onClick: () => navigate("/auth/signup")
          }
        });
      } else {
        toast.success("Saved!", {
          description: `${centerName} added to your saved list.`,
          action: {
            label: "View Saved",
            onClick: () => navigate("/favorites")
          }
        });
      }
    }
  };

  const handleGuideDownload = () => {
    analytics.guideClick('fair-deal', 'sidebar');
    window.open('/guides/fair-deal', '_blank');
  };

  return (
    <div className="bg-grandpal-beige rounded-lg p-5 mb-6 shadow-sm">
      <h2 className="text-xl font-semibold mb-2">Take the Next Step</h2>
      <p className="text-gray-700 mb-4">
        Ready to learn more about {centerName}? Save it to your list and compare options.
      </p>
      
      <div className="space-y-3">
        <Button 
          className={`w-full text-lg py-6 ${isSaved 
            ? 'bg-red-500 hover:bg-red-600' 
            : 'bg-grandpal-green-dark hover:bg-grandpal-green-darkHover'}`}
          size="lg"
          onClick={handleSaveToggle}
        >
          {isSaved ? (
            <>
              <Heart className="mr-2 h-5 w-5 fill-current" />
              Saved to List
            </>
          ) : (
            <>
              <Heart className="mr-2 h-5 w-5" />
              Save to List
            </>
          )}
        </Button>
        
        <div className="grid grid-cols-1 gap-3">
          <GuideButton onClick={handleGuideDownload} />
        </div>
        
        <SidebarLinks 
          hiqaProfileUrl={hiqaProfileUrl} 
          centerName={centerName} 
        />
      </div>
    </div>
  );
}
